/**
 * Custom Hooks para WhatsApp Settings API (Multi-Tenant)
 *
 * Backend endpoints (apps/api/src/routes/user-whatsapp-settings-routes.ts):
 * - POST /api/user/whatsapp-settings - Criar/atualizar credenciais
 * - GET /api/user/whatsapp-settings - Buscar credenciais
 * - GET /api/user/whatsapp-settings/usage - Estatísticas de uso
 * - DELETE /api/user/whatsapp-settings - Deletar credenciais
 * - POST /api/user/whatsapp-settings/validate - Validar com Meta API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFirebaseAuth } from '@/contexts/FirebaseAuthContext';
import { toast } from 'react-hot-toast';
import { auth } from '@/lib/firebase';

const API_URL =
  import.meta.env.VITE_API_URL || 'https://chatbot-imoveis-api.chatbotimoveis.workers.dev';

// ============================================================================
// TYPES (baseados no backend real)
// ============================================================================

export interface WhatsAppCredentials {
  meta_access_token: string;
  phone_number_id: string;
  webhook_verify_token: string;
  business_account_id?: string;
  display_name?: string;
  phone_number?: string;
}

export interface WhatsAppSettings {
  id: number;
  user_id: string;
  phone_number_id: string;
  business_account_id: string | null;
  display_name: string | null;
  phone_number: string | null;
  status: 'pending' | 'active' | 'invalid' | 'suspended' | 'error' | 'not_configured' | 'unknown';
  validated_at: string | null;
  last_error: string | null;
  api_version: string;
  rate_limit_per_second: number;
  rate_limit_per_minute: number;
  daily_limit: number;
  current_tier: 'tier1' | 'tier2' | 'tier3' | 'tier4' | 'unlimited';
  created_at: string;
  updated_at: string;
}

export interface WhatsAppUsageStats {
  configured: boolean;
  usage: {
    period_start?: string;
    period_end?: string;
    messages_sent: number;
    messages_received: number;
    messages_delivered: number;
    messages_read: number;
    messages_failed: number;
    conversations_business_initiated: number;
    conversations_user_initiated: number;
  };
  limits: {
    daily_limit: number;
    current_tier: string;
    rate_limit_per_second: number;
    rate_limit_per_minute: number;
    usage_percentage: number;
  } | null;
}

export interface ValidationResult {
  valid: boolean;
  message?: string;
  error?: string;
  error_code?: number;
  error_type?: string;
  phone_info?: {
    verified_name: string;
    display_phone_number: string;
    quality_rating: string;
  };
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const whatsappKeys = {
  all: ['whatsapp'] as const,
  settings: (userId: string) => [...whatsappKeys.all, 'settings', userId] as const,
  usage: (userId: string) => [...whatsappKeys.all, 'usage', userId] as const,
};

// ============================================================================
// FETCH HELPERS (padrão do projeto - lib/api.ts)
// ============================================================================

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const user = auth.currentUser;

  // Não fazer chamada se não houver usuário autenticado
  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-user-id': user.uid,
    ...options.headers,
  };

  const fullUrl = `${API_URL}${endpoint}`;

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: `Network error: ${response.status} ${response.statusText}`,
      status: response.status,
    }));

    // Apenas logar erros que NÃO são 401 (não autenticado é esperado)
    if (response.status !== 401) {
      console.error('WhatsApp API Error:', endpoint, error);
    }

    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * GET /api/user/whatsapp-settings
 * Buscar credenciais do usuário (tokens mascarados por segurança)
 */
export function useWhatsAppSettings() {
  const { user } = useFirebaseAuth();

  return useQuery({
    queryKey: whatsappKeys.settings(user?.uid || ''),
    queryFn: async () => {
      return fetchWithAuth('/api/user/whatsapp-settings') as Promise<{
        configured: boolean;
        credentials: WhatsAppSettings | null;
      }>;
    },
    enabled: !!user?.uid,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });
}

/**
 * POST /api/user/whatsapp-settings
 * Criar ou atualizar credenciais WhatsApp
 */
export function useUpdateWhatsAppSettings() {
  const { user } = useFirebaseAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: WhatsAppCredentials) => {
      return fetchWithAuth('/api/user/whatsapp-settings', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
    },
    onSuccess: () => {
      // Invalidar cache para forçar refetch
      if (user?.uid) {
        queryClient.invalidateQueries({ queryKey: whatsappKeys.settings(user.uid) });
        queryClient.invalidateQueries({ queryKey: whatsappKeys.usage(user.uid) });
      }

      toast.success('✅ Credenciais WhatsApp salvas com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`❌ Erro ao salvar: ${error.message}`);
    },
  });
}

/**
 * GET /api/user/whatsapp-settings/usage
 * Buscar estatísticas de uso WhatsApp (mensagens, conversas, limites)
 */
export function useWhatsAppUsage() {
  const { user } = useFirebaseAuth();

  return useQuery({
    queryKey: whatsappKeys.usage(user?.uid || ''),
    queryFn: async () => {
      return fetchWithAuth('/api/user/whatsapp-settings/usage') as Promise<WhatsAppUsageStats>;
    },
    enabled: !!user?.uid,
    refetchInterval: 60 * 1000, // Atualizar a cada 1 minuto
    staleTime: 30 * 1000, // Cache por 30 segundos
  });
}

/**
 * DELETE /api/user/whatsapp-settings
 * Deletar credenciais WhatsApp do usuário
 */
export function useDeleteWhatsAppSettings() {
  const { user } = useFirebaseAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return fetchWithAuth('/api/user/whatsapp-settings', {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Invalidar cache
      if (user?.uid) {
        queryClient.invalidateQueries({ queryKey: whatsappKeys.settings(user.uid) });
        queryClient.invalidateQueries({ queryKey: whatsappKeys.usage(user.uid) });
      }

      toast.success('✅ Credenciais WhatsApp removidas com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`❌ Erro ao deletar: ${error.message}`);
    },
  });
}

/**
 * POST /api/user/whatsapp-settings/validate
 * Validar credenciais com Meta Graph API antes de salvar
 */
export function useValidateWhatsAppSettings() {
  return useMutation({
    mutationFn: async (credentials: { meta_access_token: string; phone_number_id: string }) => {
      return fetchWithAuth('/api/user/whatsapp-settings/validate', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }) as Promise<ValidationResult>;
    },
    onSuccess: data => {
      if (data.valid && data.phone_info) {
        toast.success(
          `✅ Credenciais válidas!\n📱 ${data.phone_info.verified_name}\n⭐ Qualidade: ${data.phone_info.quality_rating}`
        );
      }
    },
    onError: (error: Error) => {
      toast.error(`❌ Validação falhou: ${error.message}`);
    },
  });
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Obter cor do badge de status
 */
export function getStatusColor(status?: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'invalid':
      return 'bg-red-100 text-red-800';
    case 'suspended':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Obter label do status
 */
export function getStatusLabel(status?: string): string {
  switch (status) {
    case 'active':
      return '✅ Ativo';
    case 'pending':
      return '⏳ Pendente Validação';
    case 'invalid':
      return '❌ Inválido';
    case 'suspended':
      return '🚫 Suspenso';
    default:
      return '⚠️ Não Configurado';
  }
}

/**
 * Obter label do tier
 */
export function getTierLabel(tier?: string): string {
  switch (tier) {
    case 'tier1':
      return 'Tier 1 (1k/dia)';
    case 'tier2':
      return 'Tier 2 (10k/dia)';
    case 'tier3':
      return 'Tier 3 (100k/dia)';
    case 'tier4':
      return 'Tier 4 (Ilimitado)';
    case 'unlimited':
      return 'Ilimitado';
    default:
      return 'Não definido';
  }
}

/**
 * Formatar número de telefone
 */
export function formatPhoneNumber(phone?: string): string {
  if (!phone) return 'N/A';

  // Remove tudo que não é número
  const numbers = phone.replace(/\D/g, '');

  // Formato brasileiro: +55 (11) 99999-9999
  if (numbers.startsWith('55') && numbers.length === 13) {
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9)}`;
  }

  // Retornar com + se tiver código de país
  if (numbers.length > 10) {
    return `+${numbers}`;
  }

  return phone;
}
